import { useEffect, useState } from 'react'
import { UnsupportedChainIdError } from '@web3-react/core'
import { useEagerConnect, useEthers, useInactiveListener } from '../../hooks'
import { AbstractConnector } from '@web3-react/abstract-connector'
import { WalletConnectConnector } from '@web3-react/walletconnect-connector'
import { network } from '../../utils'
import { logAmplitudeEvent } from '../../utils'
import { useConnectModal } from '../../providers/connect/context'
import { useSafeAppConnection, SafeAppConnector } from '@gnosis.pm/safe-apps-web3-react'

const safeMultisigConnector = new SafeAppConnector()

export function useConnection() {
  const { active, error, activate, account, connector: existingConnector } = useEthers()
  const { isConnectModalOpen, setConnectModalClose, setConnectModalOpen, toggleConnectModal } = useConnectModal()
  const [isWalletModalOpen, setWalletModalOpen] = useState(false)

  useEffect(() => {
    if (isConnectModalOpen) {
      logAmplitudeEvent('Wallet: Open Modal')
    }
  }, [isConnectModalOpen])

  // GnosisSafe integration
  const triedToConnectToSafe = useSafeAppConnection(safeMultisigConnector)

  // Try to eager connect to injected controller
  const triedEager = useEagerConnect(!triedToConnectToSafe || active)
  useEffect(() => {
    if (triedToConnectToSafe && triedEager && !active && !error) {
      activate(network)
    }
  }, [triedToConnectToSafe, triedEager, active, error, activate])

  // Add listeners while waiting for activation
  useInactiveListener(triedToConnectToSafe && !triedEager)

  const connect = (connector: AbstractConnector | undefined) => {
    if (connector && connector !== existingConnector) {
      // If the connector is walletconnect and the user has already tried to connect, manually reset the connector
      if (connector instanceof WalletConnectConnector && connector.walletConnectProvider?.wc?.uri) {
        connector.walletConnectProvider = undefined
      }

      activate(connector, undefined, true).catch((error) => {
        if (error instanceof UnsupportedChainIdError) {
          activate(connector)
        } else {
          console.error(error)
        }
      })
    }
    setConnectModalClose()
  }

  const disconnect = () => {
    existingConnector && (existingConnector as any).close && (existingConnector as any).close()

    setWalletModalOpen(false)
  }

  const change = () => {
    disconnect()
    setConnectModalOpen()
  }

  return {
    account,
    active,
    change,
    connect,
    disconnect,
    error,
    isConnectModalOpen,
    isWalletModalOpen,
    setConnectModalClose,
    setConnectModalOpen,
    setWalletModalOpen,
    toggleConnectModal,
  }
}
